import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DetailCardProps {
  label?: string;
  icon?: LucideIcon;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
}

export function DetailCard({
  label,
  icon: Icon,
  description,
  action,
  children,
  className,
  headerClassName,
  bodyClassName,
}: DetailCardProps) {
  const hasHeader = label || description || action;

  return (
    <section
      className={cn(
        'rounded-xl border bg-card px-4 py-4 text-sm text-card-foreground shadow-sm',
        className,
      )}
    >
      {hasHeader && (
        // `flex-wrap`: when the label and the action cannot share a line, the
        // action drops below the label instead of squeezing it into an
        // ellipsis ("Perfo…" beside "Full report" in a narrow pane). Where
        // they do fit, nothing moves.
        <div
          className={cn(
            'mb-3 flex flex-wrap items-start justify-between gap-x-3 gap-y-2',
            headerClassName,
          )}
        >
          <div className="min-w-0 grow space-y-1">
            {label && (
              <div className="flex min-w-0 items-start gap-2">
                {Icon && (
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted">
                    <Icon size={13} className="text-muted-foreground" />
                  </span>
                )}
                <h3 className="min-w-0 break-words text-sm font-semibold leading-6 text-foreground">
                  {label}
                </h3>
              </div>
            )}
            {/* `contain-inline-size` keeps the description out of the
                column's content width, so whether the action wraps is decided
                by the label alone; the description still fills the column and
                wraps inside it. Without it a one-line description would push
                the action down on every card that has one. */}
            {description && (
              <p className="text-sm leading-5 text-muted-foreground contain-inline-size">
                {description}
              </p>
            )}
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      )}
      <div className={cn('text-sm leading-6', bodyClassName)}>{children}</div>
    </section>
  );
}

interface DetailFieldProps {
  label: string;
  value?: ReactNode;
  className?: string;
  valueClassName?: string;
  stacked?: boolean;
}

export function DetailField({
  label,
  value,
  className,
  valueClassName,
  stacked = false,
}: DetailFieldProps) {
  if (stacked) {
    return (
      <div className={cn('space-y-1', className)}>
        <p className="text-sm leading-6 text-muted-foreground">{label}</p>
        <div
          className={cn(
            'min-w-0 break-words text-sm font-medium leading-6 text-foreground',
            valueClassName,
          )}
        >
          {value ?? '—'}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('@container', className)}>
      <div
        className={cn(
          'flex min-w-0 flex-col gap-1',
          // NOT-51: at >=18rem we go side-by-side. Both children get
          // `min-w-0` so flex shrinking actually breaks lines instead of
          // pushing one onto the other; `flex-wrap` is the safety net that
          // collapses to stacked layout when the label is long enough that
          // a side-by-side row would otherwise overlap the value.
          '@[18rem]:flex-row @[18rem]:flex-wrap @[18rem]:items-baseline @[18rem]:justify-between @[18rem]:gap-x-4 @[18rem]:gap-y-1',
        )}
      >
        <p className="min-w-0 break-words text-sm leading-6 text-muted-foreground @[18rem]:shrink">
          {label}
        </p>
        <div
          className={cn(
            'min-w-0 break-words text-sm font-medium leading-6 text-foreground',
            '@[18rem]:text-right',
            valueClassName,
          )}
        >
          {value ?? '—'}
        </div>
      </div>
    </div>
  );
}

export function DetailFieldGrid({
  children,
  className,
  columns = 'auto',
}: {
  children: ReactNode;
  className?: string;
  columns?: 'auto' | 1 | 2;
}) {
  const columnClass =
    columns === 1 ? 'grid-cols-1' : columns === 2 ? 'grid-cols-2' : '@lg:grid-cols-2';

  return (
    <div
      className={cn(
        '@container grid grid-cols-1 gap-x-6 gap-y-3',
        // A child that spans more columns than the grid currently has (e.g. a
        // `col-span-2` while this is still one column) makes the grid grow an
        // implicit track. Implicit tracks are `auto`-sized, and every
        // DetailField is a `container-type: inline-size` box — size containment
        // means it contributes nothing to an auto track, so the track collapses
        // to 0px and the fields placed in it render one character per line.
        // Sizing implicit columns as 1fr keeps such a track readable. Children
        // that want the whole row should use `col-span-full`, which spans the
        // explicit columns and never creates an implicit one.
        'auto-cols-fr',
        columnClass,
        className,
      )}
    >
      {children}
    </div>
  );
}
